import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus } from '@digital-twins/design-system/icons'
import { motion, useReducedMotion } from 'framer-motion'
import useSWR from 'swr'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { populationsApi } from '@/lib/api'
import { AudienceSummary } from '@/lib/api/client'
import { resolveAudienceCanonicalCoverUrl } from '@/pages/AudiencesPage/utils/audience-presentation'
import { FadeUp } from '../../FadeUp'
import { OnboardingWizardState } from '@/types/components/onboarding'
import { getTopAudiences } from './scoreAudiences'

const MAX_CARDS = 2

// --onboarding-portrait-deep corrects the dark-mode --primary-dark drift.
// See TwinPortraitStack.tsx for the full explanation.
const GRADIENTS = [
  'from-primary via-[hsl(var(--onboarding-portrait-deep))] to-chart-secondary',
  'from-[hsl(var(--onboarding-portrait-deep))] via-primary to-chart-accent',
  'from-primary via-[hsl(var(--onboarding-portrait-deep))] to-primary',
]

interface AudiencesSectionProps {
  className?: string
  /** Wizard state used to personalize which marketplace audiences are shown */
  wizardState?: OnboardingWizardState
}

/**
 * Picks up to MAX_CARDS audiences for display.
 *
 * Priority: own (canEdit) → org-shared (same-org, view-only) → ICP marketplace backfill.
 *
 * When the user has made industry/country selections during onboarding,
 * marketplace audiences are ranked by relevance via `scoreAudiences`.
 */
function selectDisplayAudiences(
  audiences: Array<AudienceSummary>,
  wizardState?: OnboardingWizardState,
): Array<AudienceSummary> {
  const own = audiences.filter((a) => a.canEdit === true)
  const orgShared = audiences.filter(
    (a) => a.canEdit !== true && (a.sharingMode === 'organization' || a.sharingMode === 'shared'),
  )
  const ownAndShared = [...own, ...orgShared]

  const hasPersonalization =
    wizardState &&
    (
      wizardState.selectedIndustries.length > 0 ||
      wizardState.selectedIndustryAncestors.length > 0 ||
      wizardState.selectedCountries.length > 0 ||
      wizardState.targetingType === 'B2B' ||
      wizardState.targetingType === 'B2C'
    )

  if (hasPersonalization) {
    // Personalized: own/shared first, then rank marketplace by ICP relevance for remaining slots.
    const remainingSlots = MAX_CARDS - Math.min(ownAndShared.length, MAX_CARDS)
    const ranked = remainingSlots > 0
      ? getTopAudiences(audiences, wizardState, remainingSlots)
      : []
    return [...ownAndShared, ...ranked].slice(0, MAX_CARDS)
  }

  // Generic fallback: own/shared first, then marketplace backfill
  if (ownAndShared.length >= MAX_CARDS) return ownAndShared.slice(0, MAX_CARDS)
  const marketplace = audiences.filter(
    (a) => a.sharingMode === 'marketplace' && !a.canEdit,
  )
  return [...ownAndShared, ...marketplace].slice(0, MAX_CARDS)
}

const CARD_HEIGHT = 'h-[178px]'

/**
 * Horizontal audience cover card — full-width landscape, matching the
 * proportions of the CinematicAudienceCard used in the audiences lobby.
 */
function AudienceCoverCard({
  audience,
  gradient,
  className,
}: {
  audience: AudienceSummary
  gradient: string
  className?: string
}) {
  const coverUrl = resolveAudienceCanonicalCoverUrl(audience)

  return (
    <div
      className={cn(
        'relative w-full overflow-hidden rounded-xl bg-gradient-to-br shadow-md transition-transform duration-300 hover:-translate-y-1',
        CARD_HEIGHT,
        gradient,
        className,
      )}
    >
      {coverUrl && (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${coverUrl})` }}
        />
      )}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(0deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)',
        }}
      />
      <div className="absolute inset-x-0 bottom-0 p-3">
        <p className="line-clamp-2 text-sm font-medium leading-tight text-white">
          {audience.name}
        </p>
      </div>
    </div>
  )
}

function PlaceholderCard({
  gradient,
  className,
}: {
  gradient: string
  className?: string
}) {
  return (
    <div
      className={cn(
        'w-full rounded-xl bg-gradient-to-br opacity-20',
        CARD_HEIGHT,
        gradient,
        className,
      )}
    />
  )
}

function CreateOwnCard({ className }: { className?: string }) {
  const { t } = useTranslation('onboarding')
  return (
    <div
      className={cn(
        'flex w-full items-center justify-center rounded-xl border-2 border-dashed border-border/60 bg-muted/20',
        CARD_HEIGHT,
        className,
      )}
    >
      <div className="flex flex-col items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
          <Plus className="h-4 w-4 text-muted-foreground" />
        </div>
        <Text size="sm" color="muted" className="text-center">
          {t('guide.createOwn')}
        </Text>
      </div>
    </div>
  )
}

/**
 * AudiencesSection — section 1 of the Getting Started Guide.
 *
 * Shows up to 3 real audience cards (cover image + name) sourced from
 * populationsApi.list(). Org-owned ready audiences are prioritised;
 * remaining slots are backfilled with published marketplace audiences.
 * The 4th column is always the "Create your own" CTA.
 */
export function AudiencesSection({ className, wizardState }: AudiencesSectionProps) {
  const { t } = useTranslation('onboarding')
  const shouldReduceMotion = useReducedMotion()
  const motionDuration = shouldReduceMotion ? 0 : 0.5

  const { data: allAudiences, isLoading } = useSWR<Array<AudienceSummary>>(
    'home-audiences',
    async () => {
      const list = await populationsApi.list()
      return list.filter((a) => a.status === 'ready')
    },
    { revalidateOnFocus: false, revalidateOnReconnect: false },
  )

  const displayCards = useMemo(
    () => selectDisplayAudiences(allAudiences ?? [], wizardState),
    [allAudiences, wizardState],
  )

  const cards = displayCards.length > 0
    ? displayCards.map((audience, idx) => ({ key: audience.id, node: <AudienceCoverCard audience={audience} gradient={GRADIENTS[idx % GRADIENTS.length]} /> }))
    : GRADIENTS.slice(0, MAX_CARDS).map((g, idx) => ({ key: g, node: <PlaceholderCard gradient={g} /> }))

  const allItems = [
    ...cards,
    { key: 'create-own', node: <CreateOwnCard /> },
  ]

  return (
    <div className={cn('flex flex-col items-center gap-6', className)}>
      <FadeUp delay={0} className="flex flex-col gap-2 text-center">
        <Heading level={2} size="3xl">
          {t('guide.audiencesTitle')}
        </Heading>
        <Text color="muted" className="max-w-sm">
          {t('guide.audiencesBody')}
        </Text>
      </FadeUp>

      <div className="w-full">
        {isLoading ? (
          <div className="flex w-full flex-row gap-3">
            <Skeleton className={cn('flex-1 rounded-xl', CARD_HEIGHT)} />
            <Skeleton className={cn('flex-1 rounded-xl', CARD_HEIGHT)} />
            <Skeleton className={cn('flex-1 rounded-xl', CARD_HEIGHT)} />
          </div>
        ) : (
          <div
            className="flex w-full flex-row gap-3"
            data-testid="audiences-card-grid"
          >
            {allItems.map(({ key, node }, idx) => (
              <motion.div
                key={key}
                className="flex-1 min-w-0"
                initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 50, scale: shouldReduceMotion ? 1 : 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{
                  delay: shouldReduceMotion ? 0 : 0.3 + idx * 0.15,
                  duration: motionDuration,
                  ease: 'easeOut',
                }}
              >
                {node}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
